import { Component, OnDestroy, OnInit } from '@angular/core'
import { MessageService } from 'primeng/api'
import { Subject, takeUntil } from 'rxjs'
import { ViewChild } from '@angular/core'

import { LeaseLedgerDialogComponent } from '../../components/lease-ledger-dialog/lease-ledger-dialog.component'
import { CarteraRow, CarteraView, MonthlyCloseResult, MoraReport } from '../../data/interfaces/f098.interface'
import { CobranzaShellService } from '../../data/services/view/cobranza-shell.service'
import { entregarDescarga } from '../../data/utils/entregar-descarga.util'
import { ApiF098Service } from '../../data/services/api/api-f098.service'
import { AuthService } from '@data/services/modules/auth/auth.service'

/** Por debajo de un centavo, el rollforward cuadra. */
const TOLERANCIA = 0.01

/**
 * CARTERA del mes: con qué saldo arrancó, qué se cobró y con cuál cerró.
 *
 * ## Las dos lecturas, y por qué la pantalla las distingue
 *
 * - **Mes abierto** → la cartera VIVA. Cambia con cada movimiento, y eso es correcto.
 * - **Mes cerrado** → la FOTOGRAFÍA congelada. Ya no cambia aunque el arrendamiento sí.
 *
 * La diferencia se dice con todas las letras en el encabezado. Un usuario que no sepa cuál está
 * mirando no puede interpretar por qué la cifra de agosto es distinta a la que recordaba.
 *
 * ## El rollforward es una VERIFICACIÓN, no una cuenta
 *
 * `saldo inicial − cobrado − saldo final` debería dar cero. Cuando no da, **no se corrige nada**: se
 * enseña la diferencia. El devengo de mora y una edición de saldos la mueven legítimamente. Por eso
 * la columna se llama «diferencia» y no «error».
 */
@Component({
  selector: 'app-cartera',
  templateUrl: './cartera.component.html',
  styleUrls: ['./cartera.component.scss'],
})
export class CarteraComponent implements OnInit, OnDestroy {
  cartera: CarteraView | null = null
  loading = false

  /** Solo los que no cuadran. Es el interruptor que convierte la tabla en una lista de pendientes. */
  soloDescuadres = false

  /*
   * El reporte de mora: qué se cobraría de intereses moratorios, arrendamiento por arrendamiento.
   *
   *! Estaba en la API desde el paso 3 y **no lo pintaba ninguna pantalla**. Aquí importa más que en
   *! ningún otro sitio: la tarjeta de MORA de esta misma barra dice `$0.00` porque el interruptor
   *! está apagado, y sin el reporte al lado ese cero se lee como «no hay mora» cuando lo que dice es
   *! «no se está cobrando». Son cosas muy distintas para quien cobra.
   */
  mora: MoraReport | null = null
  moraAbierta = false
  exportando = false
  exportandoMora = false

  // ── El cierre ───────────────────────────────────────────────────────────────────────────────
  mostrarCierre = false
  previa: MonthlyCloseResult | null = null
  cargandoPrevia = false
  cerrando = false

  /*
   * Por qué NO hay previa, dicho dentro del diálogo.
   *
   *! El servidor rechaza cerrar un mes que no ha terminado —«2026-09 todavía no termina»— y esa
   *! razón solo salía en un toast que se va a los tres segundos. El diálogo se quedaba en blanco:
   *! título, nada, y un botón de cerrar el mes. Quien lo abriera no tenía forma de saber si el mes
   *! no se puede cerrar, si la consulta falló o si de verdad no hay nada que congelar.
   */
  errorPrevia: string | null = null

  /** El libro mayor del contrato, para ver el «de qué» detrás del total. */
  @ViewChild(LeaseLedgerDialogComponent) ledgerDialog?: LeaseLedgerDialogComponent

  mostrarReapertura = false
  motivo = ''
  reabriendo = false

  private readonly destroy$ = new Subject<void>()

  constructor(
    private readonly api: ApiF098Service,
    private readonly shell: CobranzaShellService,
    private readonly auth: AuthService,
    private readonly toast: MessageService,
  ) {}

  ngOnInit(): void {
    this.shell.month$.pipe(takeUntil(this.destroy$)).subscribe(() => this.cargar())
    this.shell.refresh$.pipe(takeUntil(this.destroy$)).subscribe(() => this.cargar())
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  get month(): string {
    const d = this.shell.month || new Date()
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
  }

  /** Cerrar y reabrir exigen `wallet:settings`, que hoy solo tiene Administración. */
  get puedeCerrar(): boolean {
    return this.auth.hasModulePermission('wallet', 'settings')
  }

  cargar(): void {
    this.loading = true
    this.api.cartera(this.month).subscribe((r) => {
      this.loading = false
      this.cartera = r.data
      if (r.error) this.toast.add({ severity: 'error', summary: 'Cartera', detail: r.msg })
    })
    //* El reporte de mora va aparte: que falle no puede dejar la pantalla sin la cartera.
    this.api.moraReport().subscribe((r) => (this.mora = r.data))
  }

  get filas(): CarteraRow[] {
    const todas = this.cartera?.rows || []
    return this.soloDescuadres ? todas.filter((f) => this.descuadra(f)) : todas
  }

  /** La hoja CARTERA con la estructura de Contabilidad: 47 columnas y los renglones de accesorios. */
  exportar(): void {
    this.exportando = true
    this.api.hoja('cartera', this.month).subscribe((d) => {
      this.exportando = false
      entregarDescarga(d, `CARTERA ${this.month}.xlsx`, this.toast, 'Cartera')
    })
  }

  /*
   * El REPORTE DE CLIENTES EN MORA, con la forma de la hoja de Contabilidad.
   *
   *! No va por mes sino por FECHA DE CORTE: se calcula a hoy. Con la mora apagada el propio archivo lo
   *! dice en su encabezado y en NOTAS, así que ya no hace falta disfrazarlo en el nombre.
   */
  exportarMora(ev: Event): void {
    ev.stopPropagation()
    this.exportandoMora = true
    this.api.clientesEnMora().subscribe((d) => {
      this.exportandoMora = false
      entregarDescarga(d, `CLIENTES EN MORA ${new Date().toISOString().slice(0, 10)}.xlsx`, this.toast, 'Clientes en mora')
    })
  }

  /** Abre el detalle contable del contrato. Solo lectura: aplicar sigue siendo del centro de pagos. */
  verLibroMayor(f: CarteraRow): void {
    this.ledgerDialog?.abrir(f.leaseId, f.contractNumber || f.leaseFolio)
  }

  descuadra(f: CarteraRow): boolean {
    return f.rollforwardDelta != null && Math.abs(f.rollforwardDelta) > TOLERANCIA
  }

  get cuantosDescuadran(): number {
    return (this.cartera?.rows || []).filter((f) => this.descuadra(f)).length
  }

  /*
   * Lo cobrado de RENTA en el mes. Solo renta, a propósito.
   *
   *! Sumaba los cinco conceptos mientras el saldo inicial, el final y la diferencia son de renta
   *! sola, así que el renglón se contradecía a la vista: `14,113,811.11 − 1,324,060.01 =
   *! 14,113,811.11` con una Diferencia de `$0.00`. Quien comprobara la resta —que es exactamente lo
   *! que Contabilidad hace— concluiría que la columna Diferencia miente. El efectivo total del mes
   *! se mira en «Entró» y en la pestaña Dinero; esta línea es el rollforward de la renta.
   */
  cobrado(f: CarteraRow): number {
    return f.collected?.rent || 0
  }

  // ── El cierre ───────────────────────────────────────────────────────────────────────────────

  /**
   * Abre el diálogo con la previsualización. **La previa no escribe nada**: dice qué congelaría.
   *
   * Se pide siempre al abrir, nunca se cachea: el punto del diálogo es ver el estado de AHORA.
   */
  abrirCierre(): void {
    this.mostrarCierre = true
    this.previa = null
    this.errorPrevia = null
    this.cargandoPrevia = true
    this.api.closePreview(this.month).subscribe((r) => {
      this.cargandoPrevia = false
      this.previa = r.data
      this.errorPrevia = r.error ? r.msg : null
      if (r.error) this.toast.add({ severity: 'error', summary: 'Cierre', detail: r.msg })
    })
  }

  confirmarCierre(): void {
    this.cerrando = true
    this.api.close(this.month).subscribe((r) => {
      this.cerrando = false
      if (r.error) return this.toast.add({ severity: 'error', summary: 'Cierre', detail: r.msg })
      this.mostrarCierre = false
      this.toast.add({ severity: 'success', summary: 'Mes cerrado', detail: `Se congelaron ${r.data?.leases ?? 0} arrendamientos de ${this.month}` })
      this.cargar()
    })
  }

  confirmarReapertura(): void {
    if (!this.motivo.trim()) return
    this.reabriendo = true
    this.api.reopen(this.month, this.motivo.trim()).subscribe((r) => {
      this.reabriendo = false
      if (r.error) return this.toast.add({ severity: 'error', summary: 'Reapertura', detail: r.msg })
      this.mostrarReapertura = false
      this.motivo = ''
      this.toast.add({ severity: 'success', summary: 'Mes reabierto', detail: `${r.data?.reopened ?? 0} fotografías quedaron marcadas como reabiertas` })
      this.cargar()
    })
  }
}
